import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import practiceClient from '../../client/PracticeClient';
import { setPractice } from '../../redux/actions/practiceActions';
import PracticeFormatRow from '../../components/Practice/PracticeFormatRow';
import LearningMethodPanel from '../../components/Practice/LearningMethodPanel';
import LearningSettingsPanel from '../../components/Practice/LearningSettingsPanel';
import DeadlinePanel from '../../components/Practice/DeadlinePanel';
import { boxCountFor } from '../../components/Practice/BoxDistributionBar';
import { CARD_PRACTICE_TYPE, CARD_PRACTICE_FORMAT } from '../../constants/cardPractice';
import useTranslation from '../../hooks/useTranslation';
import useDeckPracticeSummary from '../../hooks/useDeckPracticeSummary';
import './WordLearnPage.css';

/**
 * Страница выбора карточной практики.
 *
 * Формат у карточек один, поэтому список из одной строки — но строка та же самая, что у
 * словаря: одинаковые экраны вместо двух похожих. Раньше здесь была своя карточка со
 * своими кнопками, «облаком» обновления, которое ничего не обновляло, и режимом
 * настроек, где «применить» и «отменить» не меняли ничего.
 *
 * Сводка — чтение; init остаётся на кнопке в строке.
 */
const CardLearnPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);
    const deckId = selectedDeck?.deckId;
    const [isStartingPlanned, setIsStartingPlanned] = useState(false);

    // Методику колоды выбирает панель ниже; до её загрузки открывать практику нечем —
    // документ практики опознаётся тройкой, и методика входит в неё.
    const [learningMethod, setLearningMethod] = useState(null);

    const { summary, isLoading, practiceFor, reload } = useDeckPracticeSummary(
        deckId, CARD_PRACTICE_TYPE, t('practice.summaryFailed'));

    /**
     * Занятие по плану к дате. Отдельный запуск, а не кнопка в строке: режим набирает пул
     * сегодняшней долей плана, и знает о нём панель, а не формат.
     */
    const startPlanned = async () => {
        if (!learningMethod) {
            return;
        }
        setIsStartingPlanned(true);
        try {
            const response = await practiceClient.initializePractice({
                deckId,
                practiceType: CARD_PRACTICE_TYPE,
                practiceFormat: CARD_PRACTICE_FORMAT,
                learningMethod,
                sessionMode: 'DEADLINE_REVIEW',
            });
            dispatch(setPractice(
                response.practiceId, CARD_PRACTICE_TYPE, CARD_PRACTICE_FORMAT, learningMethod));
            navigate('/card-run');
        } catch (error) {
            console.error('Не удалось начать занятие по плану:', error);
            message.error(t('practice.initFailed'));
            setIsStartingPlanned(false);
        }
    };

    const reviewIntervalDays = summary?.reviewIntervalDays ?? null;
    const boxCount = boxCountFor(reviewIntervalDays);
    // Одна картина на колоду, и живёт она в панели методики: коробка — свойство слова.
    const deckBoxSize = summary?.deckBoxSize ?? null;

    return (
        <div className="wordLearn">
            <div className="wordLearn__head">
                <h4 className="wordLearn__title">
                    {t('practice.choosePractice')}
                    {summary && (
                        <span className="wordLearn__meta">
                            {t('practice.cardsCount', { count: summary.itemCount })}
                        </span>
                    )}
                </h4>
            </div>

            <LearningMethodPanel
                deckId={deckId}
                practiceType={CARD_PRACTICE_TYPE}
                onMethodChange={setLearningMethod}
            />

            {/* План к дате работает и здесь: карточки ведёт та же стратегия, что и слова,
                а «успею ли к экзамену» — вопрос не только словарной колоды. */}
            <DeadlinePanel
                deckId={deckId}
                practiceType={CARD_PRACTICE_TYPE}
                isStarting={isStartingPlanned}
                onStartPlanned={startPlanned}
            />

            {/* Удержка и «новых в день» — настройки обучения, а не словаря: адаптивные
                интервалы выбираются и на карточках, и без ползунка удержку там задать
                было нечем. */}
            <LearningSettingsPanel />

            <div className="practiceList">
                <PracticeFormatRow
                    title={t('practice.selfAssessment')}
                    description={t('practice.formatDescription.selfAssessment')}
                    practiceRequest={{
                        deckId,
                        practiceType: CARD_PRACTICE_TYPE,
                        practiceFormat: CARD_PRACTICE_FORMAT,
                        learningMethod,
                    }}
                    itemCount={summary?.itemCount ?? 0}
                    practice={practiceFor(CARD_PRACTICE_FORMAT, learningMethod)}
                    boxSize={deckBoxSize}
                    isLoading={isLoading || !learningMethod}
                    isLatest={false}
                    boxCount={boxCount}
                    reviewIntervalDays={reviewIntervalDays}
                    onStarted={() => navigate('/card-run')}
                    onReset={reload}
                />
            </div>
        </div>
    );
};

export default CardLearnPage;
